import { useEffect, useState } from 'react';
import { useObservable, useObservableState } from 'observable-hooks';
import { RxCollection } from 'rxdb';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { CombinedFeedbackData } from '@mopla/data-models';

import {
	EFeedbacksActionTypes,
	fetchMoplaLegFeedback,
} from '../actions/feedbacksActions';
import { useBusinessLayer } from '../business-logic';

export const useMoplaLegsFeedbacks = (
	moplaLegsIds: string[],
	isEnabled = true
) => {
	const { db, dispatch, watchActions$ } = useBusinessLayer();
	const [data, setData] = useState<CombinedFeedbackData[] | null>(null);

	useEffect(() => {
		if (!moplaLegsIds?.length) return;

		const liveQuery = (
			db['feedbacks'] as RxCollection<CombinedFeedbackData>
		).findByIds(moplaLegsIds).$;

		/** There's a gotcha here
		 * liveQuery will always emit an event once subscribed. This will emit an empty array [] and set it to the {data} store.
		 * Which is basically wrong, because [] is not an api response, but just an empty result of the liveQuery.
		 * We should find a way how to properly align a result of the liveQuery with the corresponding effect (or api fetching) status:
		 * status: not started -> data: undefined  (until query is not completed we do not know if the data exists or not)
		 * status: in progress -> data: undefined or defined (both are possible, because in might be not only first time fetching)
		 * status: success -> data: defined (here we know for sure if the data is empty or not, because we have it)
		 * */
		const s = liveQuery.subscribe((docsMap) => {
			setData(Array.from(docsMap.values()).map((doc) => doc.toJSON()));
		});

		return () => {
			s.unsubscribe();
		};
	}, [db, moplaLegsIds]);

	const isLoading$ = useObservable(
		() =>
			watchActions$([
				EFeedbacksActionTypes.FetchMoplaLegFeedback,
				EFeedbacksActionTypes.FetchMoplaLegFeedbackResult,
			]).pipe(
				map(({ type }) => type === EFeedbacksActionTypes.FetchMoplaLegFeedback),
				startWith(false),
				distinctUntilChanged()
			),
		[]
	);

	const isError$ = useObservable(
		() =>
			watchActions$(EFeedbacksActionTypes.FetchMoplaLegFeedbackResult).pipe(
				map(({ payload }) => !!payload?.error),
				startWith(false),
				distinctUntilChanged()
			),
		[]
	);

	const isLoading = useObservableState(isLoading$);
	const isError = useObservableState(isError$);

	const fetchData = async () => {
		if (isLoading || !isEnabled) {
			return;
		}

		/** If we don't have any data for the moplaLegs */
		if (!data?.length) {
			moplaLegsIds.forEach((moplaLegId) =>
				dispatch(fetchMoplaLegFeedback({ moplaLegId }))
			);
			return;
		}

		/** If we have already fetched some data, we'd like not to refetch it again */
		const docs = await (db['feedbacks'] as RxCollection<CombinedFeedbackData>)
			.findByIds(moplaLegsIds)
			.exec();

		moplaLegsIds
			.filter((id) => !docs.has(id))
			.forEach((moplaLegId) => dispatch(fetchMoplaLegFeedback({ moplaLegId })));
	};

	useEffect(() => {
		isEnabled && fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEnabled]);

	return {
		data,
		isLoading: isLoading && !data,
		isError,
	};
};
